import { useState } from 'react';
import { FaUser, FaPhone, FaEnvelope, FaIdCard, FaCheckCircle } from 'react-icons/fa';

const CibilScore = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    pan: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [showScore, setShowScore] = useState(false);
  const [cibilScore, setCibilScore] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.fullName || !formData.phone || !formData.email || !formData.pan) {
      alert('Please fill in all fields');
      return;
    }

    // Mock CIBIL score generation (in real scenario, this would call an API)
    const mockScore = Math.floor(Math.random() * (900 - 300 + 1)) + 300;
    setCibilScore(mockScore);
    // Note: This is a demo/mock score. For accurate results, integrate with actual CIBIL API
    
    setSubmitted(true);
    setShowScore(true);
  };

  const getScoreStatus = (score) => {
    if (score >= 750) {
      return { status: 'Excellent', color: '#28A745', description: 'Great credit health! You qualify for the best loan offers.' };
    } else if (score >= 700) {
      return { status: 'Good', color: '#17A2B8', description: 'Good credit score. You are eligible for most loan products.' };
    } else if (score >= 650) {
      return { status: 'Fair', color: '#FFC107', description: 'Fair credit score. Some loan options available.' };
    } else {
      return { status: 'Poor', color: '#DC3545', description: 'Low credit score. Limited loan options. Contact us for guidance.' };
    }
  };

  const scoreStatus = cibilScore ? getScoreStatus(cibilScore) : null;

  return (
    <div className="cibil-page">
      <div className="cibil-container">
        <div className="cibil-header">
          <h1>Check Your CIBIL Score</h1>
          <p>Get your free CIBIL credit score in seconds</p>
        </div>

        {!showScore ? (
          <div className="cibil-form-section">
            <div className="form-card">
              <h2>Enter Your Details</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>
                    <FaUser className="form-icon" /> Full Name
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FaPhone className="form-icon" /> Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your 10-digit phone number"
                    pattern="[0-9]{10}"
                    maxLength="10"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FaEnvelope className="form-icon" /> Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FaIdCard className="form-icon" /> PAN
                  </label>
                  <input
                    type="text"
                    name="pan"
                    value={formData.pan}
                    onChange={handleChange}
                    placeholder="Enter your PAN (e.g., ABCDE1234F)"
                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                    maxLength="10"
                    required
                  />
                </div>

                <button type="submit" className="cibil-submit-btn">
                  Check My CIBIL Score
                </button>
              </form>

              <div className="form-note">
                <p>✓ Free and secure check</p>
                <p>✓ No impact on your credit score</p>
                <p>✓ Instant results</p>
                <p style={{color: '#ff6b6b', fontSize: '0.85rem', marginTop: '0.5rem'}}>ℹ️ This is a demo score. For actual CIBIL verification, please visit CIBIL official website.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="cibil-result-section">
            <div className="result-card">
              <div className="result-header">
                <FaCheckCircle className="check-icon" />
                <h2>Your CIBIL Score</h2>
              </div>

              <div className="score-display">
                <div 
                  className="score-circle"
                  style={{ borderColor: scoreStatus.color }}
                >
                  <span 
                    className="score-number"
                    style={{ color: scoreStatus.color }}
                  >
                    {cibilScore}
                  </span>
                  <span className="score-label">out of 900</span>
                </div>
              </div>

              <div 
                className="score-status"
                style={{ borderLeft: `5px solid ${scoreStatus.color}` }}
              >
                <h3 style={{ color: scoreStatus.color }}>
                  {scoreStatus.status} Score
                </h3>
                <p>{scoreStatus.description}</p>
              </div>

              <div className="score-info">
                <h4>What This Score Means:</h4>
                <ul>
                  <li>750+ : Excellent credit health</li>
                  <li>700-749 : Good credit score</li>
                  <li>650-699 : Fair credit score</li>
                  <li>Below 650 : Poor credit score</li>
                </ul>
              </div>

              <div className="result-actions">
                <button 
                  className="apply-now-btn"
                  onClick={() => window.location.href = '/apply'}
                >
                  Apply for a Loan
                </button>
                <button 
                  className="check-again-btn"
                  onClick={() => {
                    setShowScore(false);
                    setSubmitted(false);
                    setFormData({ fullName: '', phone: '', email: '', pan: '' });
                  }}
                >
                  Check Another Score
                </button>
              </div>

              <div className="cibil-disclaimer">
                <p><strong>Disclaimer:</strong> This is a mock score for demonstration purposes. For your actual CIBIL score, please visit the official CIBIL website.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .cibil-page {
          min-height: calc(100vh - 200px);
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 3rem 2rem;
        }

        .cibil-container {
          max-width: 600px;
          margin: 0 auto;
        }

        .cibil-header {
          text-align: center;
          margin-bottom: 3rem;
          color: var(--federal-blue);
        }

        .cibil-header h1 {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        .cibil-header p {
          font-size: 1.1rem;
          color: var(--text-secondary);
        }

        .form-card, .result-card {
          background: var(--federal-white);
          border-radius: 15px;
          padding: 2.5rem;
          box-shadow: 0 10px 40px rgba(0, 47, 108, 0.15);
        }

        .form-card h2, .result-card h2 {
          color: var(--federal-blue);
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: flex;
          align-items: center;
          font-weight: 600;
          color: var(--federal-blue);
          margin-bottom: 0.5rem;
        }

        .form-icon {
          margin-right: 0.5rem;
          color: var(--federal-yellow);
        }

        .form-group input {
          width: 100%;
          padding: 0.8rem;
          border: 2px solid var(--federal-medium-gray);
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--federal-blue);
          box-shadow: 0 0 0 3px rgba(0, 47, 108, 0.1);
        }

        .cibil-submit-btn {
          width: 100%;
          padding: 1rem;
          background: var(--federal-blue);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
        }

        .cibil-submit-btn:hover {
          background: #004B9F;
          transform: translateY(-2px);
          box-shadow: 0 5px 20px rgba(0, 47, 108, 0.3);
        }

        .form-note {
          margin-top: 2rem;
          padding: 1rem;
          background:#f0f7ff;
          border-radius: 8px;
          border-left: 4px solid var(--federal-yellow);
        }

        .form-note p {
          color: var(--federal-blue);
          font-weight: 600;
          margin: 0.5rem 0;
        }

        .result-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .check-icon {
          font-size: 3rem;
          color: var(--federal-yellow);
          margin-bottom: 1rem;
        }

        .score-display {
          text-align: center;
          margin: 2rem 0;
        }

        .score-circle {
          width: 200px;
          height: 200px;
          border-radius: 50%;
          border: 8px solid var(--federal-blue);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          background: linear-gradient(135deg, #f5f7fa 0%, #e0e7ff 100%);
        }

        .score-number {
          font-size: 3rem;
          font-weight: 800;
        }

        .score-label {
          font-size: 0.9rem;
          color: var(--text-secondary);
          margin-top: 0.5rem;
        }

        .score-status {
          background: #f9f9f9;
          padding: 1.5rem;
          border-radius: 8px;
          margin: 2rem 0;
        }

        .score-status h3 {
          margin-top: 0;
        }

        .score-info {
          background: #f0f7ff;
          padding: 1.5rem;
          border-radius: 8px;
          margin: 1.5rem 0;
        }

        .score-info h4 {
          color: var(--federal-blue);
          margin-top: 0;
        }

        .score-info ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .score-info li {
          color: var(--text-secondary);
          padding: 0.5rem 0;
          padding-left: 1.5rem;
          position: relative;
        }

        .score-info li:before {
          content: "✓";
          position: absolute;
          left: 0;
          color: var(--federal-yellow);
          font-weight: bold;
        }

        .result-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin: 2rem 0;
        }

        .apply-now-btn, .check-again-btn {
          padding: 0.8rem;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.95rem;
        }

        .apply-now-btn {
          background: var(--federal-blue);
          color: white;
        }

        .apply-now-btn:hover {
          background: #004B9F;
          transform: translateY(-2px);
        }

        .check-again-btn {
          background: var(--federal-yellow);
          color: var(--federal-blue);
        }

        .check-again-btn:hover {
          background: #FFA000;
          transform: translateY(-2px);
        }

        .cibil-disclaimer {
          text-align: center;
          margin-top: 2rem;
          padding: 1rem;
          background: #f5f5f5;
          border-radius: 8px;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        @media (max-width: 600px) {
          .form-card, .result-card {
            padding: 1.5rem;
          }

          .cibil-header h1 {
            font-size: 1.8rem;
          }

          .score-circle {
            width: 150px;
            height: 150px;
          }

          .score-number {
            font-size: 2rem;
          }

          .result-actions {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default CibilScore;
